import { format, isValid, parseISO } from 'date-fns';
import delay from 'lodash/delay';
import uniq from 'lodash/uniq';
import { Fragment, ReactElement, memo, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import usePrevious from '~/hooks/usePrevious';

import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
} from '~/services/api/clientAllocation/types';
import useClientOptionsByTeams from '~/store/client/hooks/useClientOptionsByTeams';
import { IClientOptionByTeam } from '~/store/client/types';
import { useStoreState } from '~/store/hooks';
import { IUserRole } from '~/types';
import variables from '~/ui/assets/styles/colors.module.scss';
import { IOption } from '~/ui/components/inputs/Select/types';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';
import MenuList from '~/ui/components/inputs/SelectWithoutAnimation/components/MenuList';
import ClientMultiSelect from '~/ui/pages/Clients/reusable/ClientMultiSelect';
import {
  ITeamMemberMapped,
  formValuesMapper,
  formatLocationGroupOptions,
} from '~/utils/clientAllocation';
import extractFullName from '~/utils/text/extractFullName';
import AccordionMembers from './components/AccordionMembers';
import MultiValue from './components/MultiValue';
import Option from './components/Option';

import { AllocationMode } from './constants/allocationMode';
import extractLocationGroupClientsIds from './helpers/extractLocationGroupClientsIds';
import filterNumberArray from './helpers/filterNumberArray';
import { getAllocationIdByUserId, getClientsFormFieldName } from './helpers/getters';
import { IFormDynamicValues } from './validate';

interface IProps {
  members: ITeamMemberMapped[];
  clients: IOption[];
  locationGroups: IAllocationClientsByGroup[];
  clientAllocationDate?: string;
  clientAllocations?: IClientAllocationDetailed[];
  isDuplicatingLocationGroups?: boolean;
  isGlobalUser: boolean;
  assignedClients: { [key: string]: number[] };
  datesValidation?: {
    users?: {
      userId: number;
      unavailableDates: string[];
      isDifferentTeam?: boolean;
    }[];
  };
  clinicId: string;
  clientsList?: IClientOptionByTeam[];
  isActTeamLeader: boolean;
  validateAvailability?: boolean;
  isEdit?: boolean;
  isCrisis?: boolean;
}

const MembersFields = ({
  members,
  clients,
  clientAllocationDate,
  clientAllocations = [],
  isGlobalUser,
  assignedClients,
  datesValidation,
  clinicId,
  locationGroups,
  clientsList,
  isDuplicatingLocationGroups,
  isActTeamLeader,
  validateAvailability,
  isEdit,
  isCrisis,
}: IProps): ReactElement => {
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
  } = useFormContext();

  const { availableActTeams } = useStoreState(state => state.actTeam);
  const { current } = useStoreState(store => store.client.clientOptionsByTeam);
  const { loading, items, loadMore, setClientOptionsTeamIds, setCurrentClientOption } =
    useClientOptionsByTeams();

  const { includeWeekends, dateRange: range, allocationMode, ...restValues } = watch();

  const prevAllocationMode = usePrevious(allocationMode);

  const locationGroupsOptions = useMemo(
    () => formatLocationGroupOptions(locationGroups),
    [locationGroups],
  );

  // Handle allocationMode changes
  useEffect(() => {
    if (prevAllocationMode !== undefined && prevAllocationMode !== allocationMode) {
      // Clear client options when mode changes
      setCurrentClientOption([]);

      if (!isDuplicatingLocationGroups) {
        // Clear all client and group assignments
        Object.keys(restValues).forEach(value => {
          if (value.startsWith('clients') || value.startsWith('groups')) {
            setValue(value, undefined);
          }
        });
      }
      clearErrors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationMode, prevAllocationMode, isDuplicatingLocationGroups]);

  useEffect(() => {
    const availableTeamIds = availableActTeams.map(t => t.id);

    if (isGlobalUser) {
      setClientOptionsTeamIds(availableTeamIds); // set client select available teams from which to get clients
    }
  }, [availableActTeams, isGlobalUser, setClientOptionsTeamIds]);

  useEffect(() => {
    if (isGlobalUser) {
      delay(
        () =>
          setCurrentClientOption(
            clientAllocations?.flatMap(item =>
              item.user.clients.map(client => ({
                id: client.id,
                name: extractFullName(client),
                photo: client.photo,
                team: item.team,
                firstName: client.firstName,
                lastName: client.lastName,
              })),
            ),
          ),
        0,
      );
    }
  }, [clientAllocations, isGlobalUser, setCurrentClientOption]);

  const unique = useMemo(() => new Set(formValuesMapper(assignedClients)), [assignedClients]);

  const handleRemoveClient = (id: number, field: string) => {
    const newClients = filterNumberArray(assignedClients[field], id);
    if (isGlobalUser) {
      setCurrentClientOption(current.filter(client => client.id !== id));
    }
    setValue(field as any, newClients, { shouldDirty: true });
  };

  const handleRemoveGroup = (id: number, field: string) => {
    const groupField = `groups${field}`;
    const clientField = `clients${field}`;
    const groupClientsIds = extractLocationGroupClientsIds(locationGroups, id);

    const newGroups = filterNumberArray(restValues[groupField], id);

    const newClients =
      assignedClients[clientField]?.filter(item => !(groupClientsIds || []).includes(item)) || [];

    setValue(clientField as any, newClients, { shouldDirty: true });
    setValue(groupField as any, newGroups, { shouldDirty: true });
  };

  const handleRemoveClientInLocationGroup = (id: number, field: string, groupName?: string) => {
    const clientField = `clients${field}`;
    const newClients = filterNumberArray(assignedClients[clientField], id);

    let locationGroupId;
    if (!newClients.length && groupName) {
      locationGroupId = locationGroups.find(group => group.locationGroup.name === groupName)
        ?.locationGroup.id;
    } else {
      // Find the specific group that contains the removed client
      const groupWithRemovedClient = locationGroups.find(group =>
        group.clients.some(client => client.id === id),
      );

      if (groupWithRemovedClient) {
        // Check if ALL clients from this specific group are now removed
        const allGroupClientsRemoved = groupWithRemovedClient.clients.every(
          client => !newClients.includes(client.id),
        );

        if (allGroupClientsRemoved) {
          locationGroupId = groupWithRemovedClient.locationGroup.id;
        }
      }
    }

    if (locationGroupId) {
      handleRemoveGroup(locationGroupId, field);
    }

    setValue(clientField as any, newClients, { shouldDirty: true });
  };

  const handleSelectClients = (options: IClientOptionByTeam[], field: string) => {
    const newClients = options.map(o => o.id);
    setValue(field as any, newClients, { shouldDirty: true });
  };

  const handleSelectGroup = (id: number, field: string) => {
    const groupField = `groups${field}`;
    const clientField = `clients${field}`;

    const isSelectedGroup = restValues[groupField]?.includes(id);

    const groupClientsIds = extractLocationGroupClientsIds(locationGroups, id);

    if (isSelectedGroup) {
      const newClients =
        assignedClients[clientField]?.filter(item => !(groupClientsIds || []).includes(item)) || [];

      setValue(clientField as keyof IFormDynamicValues, newClients, { shouldDirty: true });
    } else {
      const newValue = Array.from(
        new Set([...groupClientsIds, ...(assignedClients[clientField] || [])]),
      );

      setValue(clientField as keyof IFormDynamicValues, newValue, { shouldDirty: true });
    }
  };

  const additionalStyleHandler = () => ({
    option: (provided: any, { data }: any) => {
      const notSelected = !unique?.has(data?.value);

      return {
        ...provided,
        backgroundColor: notSelected ? '#FFF1EE' : 'inherit',
        fontWeight: '300',
        color: variables.colorBlack,
      };
    },
  });

  const renderClientSelect = (teamMember: ITeamMemberMapped, formFieldName: string) => {
    if (isGlobalUser) {
      return (
        <ClientMultiSelect
          loading={loading}
          loadMore={loadMore}
          label="Select Clients to Assign"
          disabled={isCrisis && isEdit}
          customComponents={{ MenuList }}
          additionalStyleHandler={additionalStyleHandler}
          onSelect={v => handleSelectClients(v as IClientOptionByTeam[], `clients${formFieldName}`)}
        />
      );
    }

    return (
      <SelectComponent
        closeMenuOnSelect={false}
        showCustomComponents
        hideSelectedOptions={false}
        isDisabled={isCrisis && isEdit}
        additionalStyleHandler={() => additionalStyleHandler()}
        isMulti
        label="Select Clients to Assign"
        options={clients}
        disableMenu={teamMember.isDeleted}
        name={`clients${formFieldName}`}
        control={control}
        errors={errors}
      />
    );
  };

  const renderSelect = (teamMember: ITeamMemberMapped, formFieldName: string) => (
    <Fragment key={teamMember.id}>
      <div>
        {allocationMode === AllocationMode.BY_CLIENT ? (
          renderClientSelect(teamMember, formFieldName)
        ) : (
          <SelectComponent
            closeMenuOnSelect={false}
            showCustomComponents
            hideSelectedOptions={false}
            additionalStyleHandler={() => additionalStyleHandler()}
            isMulti
            label="Select Location Group"
            options={locationGroupsOptions}
            disableMenu={teamMember.isDeleted}
            name={`groups${formFieldName}`}
            control={control}
            errors={errors}
            isDisabled={isCrisis && isEdit}
            customComponents={{
              MenuList: props => <MenuList {...props} label="Location Groups" />,
              MultiValue: props => (
                <MultiValue
                  {...props}
                  clientIds={assignedClients[`clients${formFieldName}`]}
                  locationGroups={locationGroups}
                  onDelete={id => handleRemoveClient(id, `clients${formFieldName}`)}
                  onDeleteGroup={id => handleRemoveGroup(id, formFieldName)}
                />
              ),
              Option: props => (
                <Option {...props} selectGroup={id => handleSelectGroup(id, formFieldName)} />
              ),
            }}
          />
        )}
      </div>
    </Fragment>
  );

  return (
    <>
      {members.map(teamMember => {
        const errorDates = uniq(
          datesValidation?.users
            .find(user => user.userId === teamMember.id)
            ?.unavailableDates.map(item => {
              const dateString = parseISO(item.split('T')[0]);

              if (dateString && isValid(dateString)) {
                return format(dateString, 'MM-dd-yyyy');
              }

              return null;
            })
            .filter(Boolean) || [],
        );

        const showDifferentTeamErrorMessage = !!datesValidation?.users.find(
          user => user.userId === teamMember.id && user.isDifferentTeam,
        );

        const hasGlobalUserRole = !!teamMember.roles
          ?.filter(r => r.clinic.id === Number(clinicId))
          .find(r => r.name === IUserRole.GlobalUser);

        const currentAllocation = getAllocationIdByUserId(teamMember.id, clientAllocations);
        const formFieldName = getClientsFormFieldName(teamMember?.id, currentAllocation?.id);

        const list = useMemo(() => {
          if (clientsList && !isGlobalUser && !isActTeamLeader) {
            return clientsList;
          }
          return items;
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [items, clientsList, isGlobalUser, isActTeamLeader]);

        return (
          <AccordionMembers
            validateAvailability={validateAvailability}
            key={teamMember.id}
            member={teamMember}
            clientsList={list}
            renderSelect={() => renderSelect(teamMember, formFieldName)}
            hasGlobalUserRole={hasGlobalUserRole}
            clientAllocationDate={clientAllocationDate}
            errorDates={errorDates}
            showDifferentTeamErrorMessage={showDifferentTeamErrorMessage}
            allocationMode={allocationMode}
            locationGroups={locationGroups}
            handleRemoveClient={handleRemoveClient}
            handleRemoveClientInLocationGroup={handleRemoveClientInLocationGroup}
            formFieldName={formFieldName}
            clientAllocationsList={clientAllocations.filter(
              allocation => allocation.user.id === teamMember.id,
            )}
          />
        );
      })}
    </>
  );
};

export default memo(MembersFields);
